import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import {
  EConsentMethod,
  EReportType,
  ERunReportQueryParam,
  ERunReportStep,
} from '@/pages/RunReport/types'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setSocialMedia,
  setSubjectInfo,
} from '@/store/api/RunReport/RunReport.slice'
import type { confirmSubjectDetailsSchemaType } from '@/utils/schemas'
import { confirmSubjectDetailsSchema } from '@/utils/schemas'

import { ProfilePictureField } from '../../components/ProfilePictureField'
import { SocialMediaAccountsField } from '../../components/SocialMediaAccountsField'

export const ConfirmSubjectDetails = () => {
  useRunReportGuard(ERunReportStep.ConfirmSubjectDetails)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const isAdverseMedia =
    slice.selectSearchType.reportType === EReportType.AdverseMediaReport
  const isDelegateFlow =
    slice.useCases.consentSwitchValue &&
    slice.consent.method === EConsentMethod.Delegate

  const form = useForm<confirmSubjectDetailsSchemaType>({
    resolver: zodResolver(confirmSubjectDetailsSchema),
    defaultValues: {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
      email: slice.subjectInfo.email,
      socialMedia: slice.socialMedia,
    },
    mode: 'onChange',
  })

  const { handleSubmit, setValue, control, trigger, watch } = form

  const watchedValues = watch()
  const isFormValid =
    confirmSubjectDetailsSchema.safeParse(watchedValues).success

  useEffect(() => {
    const formFields: Partial<
      Record<keyof confirmSubjectDetailsSchemaType, unknown>
    > = {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
      email: slice.subjectInfo.email,
      socialMedia: slice.socialMedia,
    }
    const hydratedKeys: (keyof confirmSubjectDetailsSchemaType)[] = []
    Object.entries(formFields).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        const typedKey = key as keyof confirmSubjectDetailsSchemaType
        setValue(typedKey, value as never, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        hydratedKeys.push(typedKey)
      }
    })
    if (hydratedKeys.length > 0) trigger(hydratedKeys)
  }, [slice.subjectInfo, slice.socialMedia, setValue, trigger])

  const onSubmit = (data: confirmSubjectDetailsSchemaType) => {
    dispatch(
      setSubjectInfo({
        ...slice.subjectInfo,
        image: data.image,
        email: data.email,
      }),
    )
    dispatch(setSocialMedia(data.socialMedia))
    navigate(AppRoutes.runReportRefineSearch)
  }

  const handleBack = () => {
    const params = new URLSearchParams({
      [ERunReportQueryParam.Q]: slice.searchResultsInfo.searchInput,
      [ERunReportQueryParam.UseCaseId]: slice.useCases.selectedUseCaseId,
      [ERunReportQueryParam.Platform]: 'linkedin',
      [ERunReportQueryParam.ReportType]: slice.selectSearchType.reportType,
    })
    navigate(`${AppRoutes.runReportSearchResults}?${params.toString()}`)
  }

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:items-center lg:gap-6 lg:overflow-y-auto lg:p-8"
        >
          <div className="hidden flex-col gap-3 lg:flex lg:flex-1">
            <h2 className="text-2xl font-medium text-neutralColor">
              {t('dashboard.runReport.confirmSubjectDetails.title')}
            </h2>
            <p className="text-sm text-neutralColor80">
              {t('dashboard.runReport.confirmSubjectDetails.subtitle1')}{' '}
              <strong className="font-medium">
                {t('dashboard.runReport.confirmSubjectDetails.subtitleBold')}
              </strong>
            </p>
            <p className="text-sm text-neutralColor80">
              {t('dashboard.runReport.confirmSubjectDetails.subtitle2')}
            </p>

            <div className="mt-4 flex gap-3">
              <Button variant="secondary" onClick={handleBack} type="button">
                {t('dashboard.runReport.confirmSubjectDetails.back')}
              </Button>
              <Button type="submit" disabled={!isFormValid}>
                {t('dashboard.runReport.confirmSubjectDetails.continue')}
              </Button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-6 lg:overflow-visible lg:p-0">
            <div className="flex flex-col gap-4 rounded-2xl bg-baseColorHighlight p-4">
              <ProfilePictureField
                hint={t(
                  'dashboard.runReport.confirmSubjectDetails.profilePictureHint',
                )}
              />
            </div>

            <div className="flex flex-col gap-3 rounded-2xl bg-baseColorHighlight p-4">
              <p className="text-base font-medium text-neutralColor">
                {t('dashboard.runReport.confirmSubjectDetails.emailTitle')}
              </p>
              <p className="text-sm text-neutralColor60">
                {t('dashboard.runReport.confirmSubjectDetails.emailHint')}
              </p>
              <FormField
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">
                      {t(
                        'dashboard.runReport.confirmSubjectDetails.emailTitle',
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t(
                          'dashboard.runReport.confirmSubjectDetails.emailPlaceholder',
                        )}
                        type="email"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!isAdverseMedia && (
              <div className="flex flex-col gap-3 rounded-2xl bg-baseColorHighlight p-4">
                <SocialMediaAccountsField
                  hint={t(
                    'dashboard.runReport.confirmSubjectDetails.socialMediaHint',
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.confirmSubjectDetails.title')}
              </h2>
              <p className="text-xs text-neutralColor60">
                {t('dashboard.runReport.confirmSubjectDetails.subtitle1')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                className="flex-1"
              >
                {t('dashboard.runReport.confirmSubjectDetails.back')}
              </Button>
              <Button type="submit" disabled={!isFormValid} className="flex-1">
                {t('dashboard.runReport.confirmSubjectDetails.continue')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </WeScreen>
  )
}
